import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { CircleAlert, RefreshCw } from 'lucide-react';

import { checkinAction, nextAction } from 'actions/workflow';
import { FormColumn, FormRow, Input } from 'components/Form';
import { Button } from 'components/Button';
import Modal from 'components/Modal';
import { last4SsnMask, numberUnmask } from 'utils/masks';
import useWorkflowGuard from 'hooks/useWorkflowGuard';

const WelcomeBack = ({
  navigate,
  searchParams,
  dispatch,
}) => {
  useWorkflowGuard(searchParams, navigate);
  const firstName = useSelector((state) => get(state, 'workflow.data.firstName'));
  const inputRef = useRef(null);
  const [ssn, setSsn] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorSubject, setErrorSubject] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [contactNumber, setContactNumber] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && ssn.length === 4) {
      handleSubmit(e);
    }
  };

  const handleRefresh = (e) => {
    e.preventDefault();
    setIsRefreshing(true);

    dispatch(checkinAction({
      skipActivityCheck: true,
      key: searchParams.get('key'),
      success: (res) => {
        setIsRefreshing(false);
        setErrorMessage(null);
        setErrorSubject(null);
        setSsn('');
        const el = inputRef.current?.inputElement || inputRef.current;
        if (el) el.focus();
        navigate(get(res, 'data.url'));
      },
      fail: () => {
        setIsRefreshing(false);
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setErrorMessage(null);

    dispatch(nextAction({
      key: searchParams.get('key'),
      data: { ssn },
      success: (res) => {
        navigate(get(res, 'data.url'));
        setIsSubmitting(false);
      },
      fail: (err) => {
        setIsSubmitting(false);
        setErrorMessage(get(err, 'data.failure.errors.0.message') || 'Something went wrong');
        setErrorSubject(get(err, 'data.failure.errors.0.subject'));
        setContactNumber(get(err, 'data.failure.errors.0.attributes.contactNumber'));
      }
    }))
  };

  const clearErrors = () => {
    setErrorSubject(null);
    setErrorMessage(null);
    setContactNumber(null);
  }

  return (
    <div className="flex flex-col items-center text-center mb-8">
      <div className="flex items-center justify-center gap-2 mb-4">
        <h2 className="page-title text-xl md:text-2xl text-center font-semibold text-slate-800 leading-tight">
          Hi {firstName}!
        </h2>
      </div>
      <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-8">
        For your security, please enter
        {' '}
        <br className="hidden md:block" />
        the last 4 digits of your SSN to continue
      </p>

      <FormRow className="max-w-full w-[350px] mb-6">
        <FormColumn>
          <Input
            ref={inputRef}
            label="Last 4 digits of SSN"
            inputClassName="font-numeric"
            placeholder="_____"
            autoFocus
            mask={last4SsnMask}
            value={ssn}
            onChange={(e) => setSsn(e.target.value)}
            onKeyDown={handleKeyDown}
            required
            inputMode="numeric"
          />
        </FormColumn>
      </FormRow>

      <FormRow className="max-w-full w-[350px]">
        <Button
          className="w-full"
          label="SUBMIT"
          disabled={!ssn || numberUnmask(ssn).length < 4}
          onClick={handleSubmit}
          isLoading={isSubmitting}
        />
      </FormRow>

      {errorMessage && (
        <Modal title="Error" onClose={clearErrors}>
          {errorSubject === 'linkExpired' ? (
            <RefreshCw className="h-12 w-12 mx-auto mb-3 block text-red-500" />
          ) : (
            <CircleAlert className="h-12 w-12 mx-auto mb-3 block text-red-500" />
          )}

          {errorSubject === 'linkExpired' ? (
            <>
              <p className="text-lg font-bold text-slate-800 mb-4">Looks Like Things Have Changed!</p>
              <p className="text-slate-700 mb-2">
                The status of your application has been updated, which means this link is no longer valid.
              </p>
              <p className="mb-4">
                Please call our Support Team at{' '}
                <a href={`tel:${contactNumber}`} className="whitespace-nowrap text-blue-500 font-semibold">
                  {contactNumber}
                </a>. Thank you.
              </p>
            </>
          ) : (
            <p className="text-slate-700 mb-4">{errorMessage}</p>
          )}

          <Button
            modifier="danger"
            className="w-full !py-3"
            label={errorSubject === 'InvalidActivity' ? 'Click here to refresh' : 'OK'}
            isLoading={isRefreshing}
            onClick={errorSubject === 'InvalidActivity' ? handleRefresh : clearErrors}
          />
        </Modal>
      )}
    </div>
  );
};

WelcomeBack.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default WelcomeBack;
